import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { ChangeSoldToActionTypes } from '../../../../features/sold-to-base/store/actions/sold-to-change.action';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { ArticlePrice, ArticlePriceLookup } from '../../../model';
import { distinctBufferDebounceTime, normalizeHttpError, withdrawOn } from '../../../util';
import { PriceConnector } from '../../connectors/price-connector';
import { UserActions } from '../actions';

@Injectable()
export class PriceEffects {
  loadSynchronousPrice$: Observable<UserActions.LoadArticlePriceSuccess | UserActions.LoadArticlePriceFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.PriceActionTypes.LoadArticlePrice),
      filter((action: UserActions.LoadArticlePrice) => !action.async),
      mergeMap((action: UserActions.LoadArticlePrice) => {
        return this.connector.getSynchronousPrice(action.lookup).pipe(
          map((articlePrice) => new UserActions.LoadArticlePriceSuccess(articlePrice)),
          catchError((error) => of(new UserActions.LoadArticlePriceFail(action.lookup, {}, normalizeHttpError(error))))
        );
      })
    )
  );

  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout, ChangeSoldToActionTypes.ChangeSoldToSuccess));

  loadAsynchronousPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.PriceActionTypes.LoadArticlePrice),
      filter((action: UserActions.LoadArticlePrice) => action.async),
      distinctBufferDebounceTime((action) => `${action.lookup.articleNumber}|${action.lookup.quantity}`, 750),
      mergeMap((actions: Array<UserActions.LoadArticlePrice>) => {
        const priceLookups = actions.map((a) => a.lookup);
        return this.connector.getAsynchronousPrices(priceLookups).pipe(
          mergeMap((prices: Array<ArticlePrice>) => {
            return this.mappedActions(priceLookups, prices);
          }),
          catchError((error) => {
            return merge(
              ...actions.map((p) => of(new UserActions.LoadArticlePriceFail(p.lookup, {}, normalizeHttpError(error))))
            );
          })
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(
    private actions$: Actions,
    private connector: PriceConnector
  ) {}

  private mappedActions(priceLookups: ArticlePriceLookup[], prices: Array<ArticlePrice>): Array<UserActions.PriceActions> {
    if (priceLookups.length !== prices.length) {
      const returnActions = [];
      priceLookups.forEach((pl) => {
        const index = prices.findIndex((p) => p.articleNumber === pl.articleNumber);
        if (index < 0) {
          returnActions.push(new UserActions.LoadArticlePriceFail(pl));
        } else {
          returnActions.push(this.mapOnPriceValue(prices[index]));
        }
        prices.splice(index, 1);
      });
      return returnActions;
    } else {
      return prices.map((p) => this.mapOnPriceValue(p));
    }
  }

  private mapOnPriceValue(articlePrice: ArticlePrice): UserActions.LoadArticlePriceFail | UserActions.LoadArticlePriceSuccess {
    if (articlePrice.price && parseFloat(articlePrice.price) < 0) {
      return new UserActions.LoadArticlePriceFail(
        {
          articleNumber: articlePrice.articleNumber,
          quantity: articlePrice.quantity,
          unit: articlePrice.unit.code,
        },
        articlePrice
      );
    } else {
      return new UserActions.LoadArticlePriceSuccess(articlePrice);
    }
  }
}
